// FormMetaTab — the Data setup page's first tab, "Form meta & SQL": the paged,
// searchable form list (POST /dashboard-setup/list-forms) showing each
// template's identity, its authorship timestamps, its entry count, and its SQL
// export — the table name and whether the connection is live.
//
// Its own file for the same reason FormStructureTab is one: it owns its own
// endpoint, mutations, pagination and column set (the TopicsTab/OptionSetsTab
// rule). It is mounted only while its tab is active, so its query needs no
// `skip`; it reports its total up through `onCount` for the tab badge — only
// while the search box is empty, since a narrowing must not be mistaken for
// the true entity count.
//
// THE TABLE NAME AND THE CONNECTION ARE ONE FLOW. No endpoint persists a table
// name on its own, so the name typed into DbNameCell is a DRAFT held here (the
// Switch, a different cell, has to read it), and switching the connection on is
// what commits it via POST /exports/add-vertical-export. Switching off deletes
// the export entry by its own id. Both directions confirm first.
//
// A draft survives a FAILED connect on purpose: the commonest rejection is a
// table name that already exists, and clearing the field would make the user
// retype it to fix one character. It is dropped on success, where the refetched
// row carries the real name.
//
// The Switch is never locally controlled — `checked` comes from server data, so
// it cannot desync and simply doesn't move until the list refetches. Writes
// invalidate the LIST tag rather than patching the cache optimistically: the
// query arg is the live `{ page, limit, search }`, which updateQueryData would
// have to reconstruct exactly.
//
// MANAGE SETS turns an option set (authored in Data structure) into a column,
// each cell a dropdown of that set's options. The picks are drafts here, and the
// toolbar's Save commits them all in one POST alongside which sets are enabled.
//
// That save is a DELTA — only changed picks, plus added/removed set ids — so the
// tab keeps baselines to measure against, both seeded from the list's own
// answer. A pick therefore survives a reload, a draft wins over the server
// value only until it is saved, and drafts are dropped on success because the
// refetch puts the real values back.
//
// WHICH SETS ARE COLUMNS is stated by the server, not inferred: `requireHeaders`
// (sent on page 1 only) makes list-forms answer with `headerFormSetIds`. Three
// states, and the middle one is the point — a list, an EMPTY list meaning
// nothing is selected, and ABSENT on every page past the first. So the guard is
// `if (headers)` and never `if (headers?.length)`.
//
// From it: `savedSetIds` refreshes whenever headers arrive (server truth), while
// `enabledSetIds` is seeded ONCE and user-owned after — page 1 answers with
// headers every time it is visited, and re-seeding would clobber a tick made
// before paging away and back. Saving returns to page 1 for exactly this reason:
// it is the only page that can refresh the baseline.
//
// The row carries bare ids and no text at all, so the set catalogue is the only
// source of BOTH the column header and the option label. Seeding is what unskips
// that query, and until it resolves there are NO set columns — not columns with
// empty cells. (A saved pick renders as the placeholder in a different case
// entirely: an option id the catalogue no longer lists.)
//
// The toolbar's SQL settings button opens ExportSettingsDrawer — the
// COMPANY-WIDE exports, which share this collection and its DELETE route but
// belong to no single form. Power BI views marks which forms have a view
// published — its own modal over the powerbi_export record. The row kebab
// re-imports ONE form's export through its own route, keyed by the form.
//
// Still NOT rendered, for want of a contract: sort glyphs (no orderBy param),
// the bulk Action menu, and a guard against navigating away with unsaved picks
// — its own chunk; note dbNameDrafts have always been unguarded the same way.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ColumnsMenu,
  ConfirmModal,
  DataTable,
  Icon,
  IconButton,
  Input,
  TableFooter,
} from '@/components/common';
import {
  useAddVerticalExportMutation,
  useGetDataSetupFormsQuery,
  useReimportFormVerticalExportMutation,
  useRemoveExportMutation,
  useUpdateFormMetadataMutation,
  type DataSetupForm,
  type FormSetPick,
} from '@/api/endpoints/dataSetupApi';
import { useGetFormSetSelectionQuery, type OptionSetSelection } from '@/api/endpoints/formSetsApi';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import { setColumnKey, useFormMetaColumns } from './formMetaColumns';
import { COMPANY_EXPORT_SECTIONS } from './companyExportSections';
import { pageRange, sameIds, saveErrorMsg, setsDelta, useTablePaging } from './dataSetupShared';
import DensityToggle, { type Density } from './DensityToggle';
import ManageSetsModal from './ManageSetsModal';
import ExportSettingsDrawer from './ExportSettingsDrawer';
import PowerBiViewsModal from './PowerBiViewsModal';

interface FormMetaTabProps {
  /** Page-owned, so the choice survives switching tabs. */
  density: Density;
  onDensity: (next: Density) => void;
  /** Reports the UNFILTERED total up for the tab badge — the tab owns the
   *  search-empty guard, since it owns the search. */
  onCount?: (total: number) => void;
}

const FormMetaTab = ({ density, onDensity, onCount }: FormMetaTabProps) => {
  const { t } = useTranslation();

  const { page, limit, setPage, onLimit } = useTablePaging();
  const [search, setSearch] = useState('');
  const [sqlSettingsOpen, setSqlSettingsOpen] = useState(false);
  const [powerBiOpen, setPowerBiOpen] = useState(false);
  const [manageSetsOpen, setManageSetsOpen] = useState(false);
  // Which sets are drawn as columns. Stable state, not a set minted per render —
  // ManageSetsModal re-seeds its draft from this and would never settle.
  const [enabledSetIds, setEnabledSetIds] = useState<Set<string>>(() => new Set());
  // The row kebab's Re-import, awaiting confirmation.
  const [reimportTarget, setReimportTarget] = useState<DataSetupForm | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  // Unsaved table names, by form id. See the header: these are committed by
  // connecting, not by a save of their own.
  const [dbNameDrafts, setDbNameDrafts] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{ form: DataSetupForm; next: boolean } | null>(null);

  // Picked set values, keyed `${formId}:${setId}`, and what the SERVER holds for
  // the same keys as far as this session has loaded — the baseline the delta is
  // measured against. `savedSetIds` is its equivalent for which sets are on.
  const [setValueDrafts, setSetValueDrafts] = useState<Record<string, string>>({});
  const [serverValues, setServerValues] = useState<Record<string, string>>({});
  const [savedSetIds, setSavedSetIds] = useState<Set<string>>(() => new Set());

  // Fetched only once something needs it — the modal being open, or a set
  // already drawing a column. The same skip-until-needed rule the export
  // drawers use.
  const {
    data: formSets = [],
    isLoading: setsLoading,
    isError: setsError,
  } = useGetFormSetSelectionQuery(undefined, {
    skip: !manageSetsOpen && enabledSetIds.size === 0,
  });
  const enabledSets = useMemo(
    () => formSets.filter((s) => enabledSetIds.has(s.id)),
    [formSets, enabledSetIds],
  );

  const [addVerticalExport, { isLoading: connecting }] = useAddVerticalExportMutation();
  const [removeExport, { isLoading: disconnecting }] = useRemoveExportMutation();
  const [reimportForm, { isLoading: reimporting }] = useReimportFormVerticalExportMutation();
  const [updateFormMetadata, { isLoading: savingSets }] = useUpdateFormMetadataMutation();

  // Mounted only while this tab is active, so no `skip` — switching tabs
  // remounts, and refetchOnMountOrArgChange refetches, as the skip toggle did.
  const { data, currentData, isLoading, isFetching, isError } = useGetDataSetupFormsQuery(
    { page, limit, search: debouncedSearch || undefined, requireHeaders: page === 1 },
    { refetchOnMountOrArgChange: true },
  );

  // A new search narrows the set — snap back to page 1 so the pager can't sit
  // past the end of the new list.
  useEffect(() => setPage(1), [debouncedSearch, setPage]);

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const { from, to } = pageRange(page, limit, total);

  // The tab badge wants the UNFILTERED total, so a narrowed list reports
  // nothing and the badge keeps the last true count.
  useEffect(() => {
    if (data && !debouncedSearch.trim()) onCount?.(total);
  }, [data, total, debouncedSearch, onCount]);

  // Take the selected sets from `headerFormSetIds`. See the header for the three
  // states; the short version is that `[]` is a real answer and only `undefined`
  // means "this page wasn't asked", which is why the check is bare truthiness.
  // Do NOT tighten it to `headers?.length` — that ignores "everything
  // deselected" and leaves stale ids in the baseline forever.
  const seeded = useRef(false);
  useEffect(() => {
    const headers = data?.headerFormSetIds;
    if (!headers) return;
    // A search narrows the list, and a narrowing must not be mistaken for the
    // truth — the rule the badge follows above. Here it also protects against
    // something destructive: a shrunken baseline would send the dropped ids as
    // `removedFormSetIds` on the next save.
    if (debouncedSearch.trim()) return;
    const next = new Set(headers);
    setSavedSetIds((prev) => (sameIds(prev, next) ? prev : next));
    if (!seeded.current) {
      seeded.current = true;
      setEnabledSetIds((prev) => (sameIds(prev, next) ? prev : next));
    }
  }, [data, debouncedSearch]);

  // Accumulate the server's picks across pages, so a draft made on page 1 still
  // compares correctly after paging away. Each loaded row is REBUILT rather than
  // merged: a cleared assignment is simply absent from `setValues`, and merging
  // would keep the old value forever.
  useEffect(() => {
    if (!data) return;
    setServerValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of data.items) {
        const prefix = `${f.id}:`;
        for (const k of Object.keys(next))
          if (k.startsWith(prefix) && !(k.slice(prefix.length) in f.setValues)) {
            delete next[k];
            changed = true;
          }
        for (const [setId, optionId] of Object.entries(f.setValues))
          if (next[prefix + setId] !== optionId) {
            next[prefix + setId] = optionId;
            changed = true;
          }
      }
      return changed ? next : prev;
    });
  }, [data]);

  const dbNameOf = useCallback(
    (f: DataSetupForm) => dbNameDrafts[f.id] ?? f.dbName ?? '',
    [dbNameDrafts],
  );
  const onDbNameCommit = useCallback(
    (formId: string, next: string) => setDbNameDrafts((d) => ({ ...d, [formId]: next })),
    [],
  );
  const onToggleConnection = useCallback(
    (form: DataSetupForm, next: boolean) => setConfirm({ form, next }),
    [],
  );

  // A draft wins while it exists; otherwise the row's own saved pick. This is
  // the line that makes a saved assignment visible at all.
  const setValueOf = useCallback(
    (f: DataSetupForm, s: OptionSetSelection) =>
      setValueDrafts[`${f.id}:${s.id}`] ?? f.setValues[s.id] ?? '',
    [setValueDrafts],
  );
  const onSetValueCommit = useCallback(
    (formId: string, setId: string, optionId: string) =>
      setSetValueDrafts((d) => ({ ...d, [`${formId}:${setId}`]: optionId })),
    [],
  );
  // Only real changes are pending. A pick sends its option id; a cell put back
  // to the placeholder sends NULL — but only when the server actually holds a
  // value to clear (the endpoint clears on null; a never-saved empty cell has
  // nothing to say). This keeps `setsDirty` honest both ways: a clear of a
  // saved pick lights Save up, an empty draft over an empty cell does not.
  const pendingMetadata = useMemo<FormSetPick[]>(
    () =>
      Object.entries(setValueDrafts)
        .filter(([k, v]) => v !== (serverValues[k] ?? '') && (v || serverValues[k]))
        .map(([k, v]) => {
          const [formId, formSetId] = k.split(':');
          return { formId, formSetId, formSetOptionId: v || null };
        }),
    [setValueDrafts, serverValues],
  );
  const { added: addedFormSetIds, removed: removedFormSetIds } = useMemo(
    () => setsDelta(enabledSetIds, savedSetIds),
    [enabledSetIds, savedSetIds],
  );
  const setsDirty =
    pendingMetadata.length > 0 || addedFormSetIds.length > 0 || removedFormSetIds.length > 0;
  // Drawn only once the feature is in play: this Save persists set assignments
  // and nothing else — not the table-name drafts — so a permanently dead button
  // on a page where most people never open Manage sets would misdescribe itself.
  const setsInPlay = enabledSetIds.size > 0 || savedSetIds.size > 0;

  const applySets = useCallback((next: Set<string>) => {
    setEnabledSetIds(next);
    // A set switched off loses its column, so anything drafted against it would
    // be invisible and stale — legacy pruned the same way on removal. NOTE this
    // tab's keys are two-segment `${formId}:${setId}`, so index 1 IS the set id
    // (unlike the structure tab's three-segment keys).
    setSetValueDrafts((d) =>
      Object.fromEntries(Object.entries(d).filter(([k]) => next.has(k.split(':')[1]))),
    );
  }, []);

  const runConfirm = async () => {
    if (!confirm) return;
    const { form, next } = confirm;
    try {
      if (next) {
        await addVerticalExport({ formId: form.id, tableName: dbNameOf(form).trim() }).unwrap();
        toast.success(t('data_setup_meta_connected_toast', { name: form.title }));
        // The refetched row now carries the saved name.
        setDbNameDrafts(({ [form.id]: _dropped, ...rest }) => rest);
      } else if (form.exportId) {
        await removeExport(form.exportId).unwrap();
        toast.success(t('data_setup_meta_disconnected_toast', { name: form.title }));
      }
      setConfirm(null);
    } catch (err) {
      // The server's own message is the useful part here — "that table already
      // exists" is exactly what a generic toast would throw away. The draft is
      // left alone so the name can be corrected without retyping.
      toast.error(saveErrorMsg(err, t('error_generic')));
      setConfirm(null);
    }
  };

  const runSaveSets = async () => {
    try {
      await updateFormMetadata({
        metadata: pendingMetadata,
        addedFormSetIds,
        removedFormSetIds,
      }).unwrap();
      // Drop the drafts — the invalidated LIST refetches and the rows come back
      // carrying these very values, so the cells read them from the server from
      // here on. `serverValues` re-accumulates from that same response.
      setSetValueDrafts({});
      // Drop the accumulated value baseline as well, so it re-fills from the
      // refetch. It only ever rebuilds rows present in a response, so whatever
      // it still holds for OTHER pages is now pre-save — and a later revert on
      // one of those rows would compare equal to that stale value and never be
      // sent. Re-deriving can at worst re-send a value the server already has,
      // which is idempotent; keeping it can silently swallow an edit.
      setServerValues({});
      // Advance the set baseline locally AND go back to page 1. The manual
      // advance covers the window before the refetch lands, where a stale
      // baseline would leave Save enabled and a duplicate save one click away;
      // page 1 is the only page that carries `headerFormSetIds`, so it is what
      // lets that baseline re-confirm itself against the server.
      setSavedSetIds(new Set(enabledSetIds));
      setPage(1);
      toast.success(t('data_setup_sets_saved'));
    } catch (err) {
      // Baseline untouched, so the same payload can simply be retried.
      toast.error(saveErrorMsg(err, t('error_generic')));
    }
  };

  const runReimport = async () => {
    if (!reimportTarget) return;
    try {
      await reimportForm(reimportTarget.id).unwrap();
      // Assumed asynchronous — say it started rather than claiming it finished.
      toast.success(t('data_setup_meta_reimport_started', { name: reimportTarget.title }));
      setReimportTarget(null);
    } catch (err) {
      toast.error(saveErrorMsg(err, t('error_generic')));
      setReimportTarget(null);
    }
  };

  const columns = useFormMetaColumns({
    dbNameOf,
    onDbNameCommit,
    onToggleConnection,
    onReimport: setReimportTarget,
    sets: enabledSets,
    setValueOf,
    onSetValueCommit,
  });
  // The form identity column stays: hiding it would leave rows with no subject.
  // Set columns are left out of the menu entirely — Manage sets is their
  // control, and listing them there too would be two controls for one concept.
  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    locked: ['form'],
    excluded: enabledSets.map((s) => setColumnKey(s.id)),
  });

  return (
    <>
      <div className='data-setup-page__toolbar'>
        <div className='data-setup-page__toolbar-lead'>
          <div className='data-setup-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('data_setup_meta_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='data-setup-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('data_setup_meta_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
        </div>
        <div className='data-setup-page__toolbar-trail'>
          {setsInPlay && (
            <Button
              iconStart='check'
              onClick={runSaveSets}
              loading={savingSets}
              disabled={!setsDirty}
            >
              {t('button_save')}
            </Button>
          )}
          <Button variant='secondary' iconStart='chart-column' onClick={() => setPowerBiOpen(true)}>
            {t('data_setup_powerbi_title')}
          </Button>
          <Button variant='secondary' iconStart='database' onClick={() => setSqlSettingsOpen(true)}>
            {t('data_setup_sql_title')}
          </Button>
          <Button variant='secondary' iconStart='layers' onClick={() => setManageSetsOpen(true)}>
            {t('data_setup_sets_title')}
            {enabledSetIds.size > 0 && (
              <span className='data-setup-page__sets-count'>{enabledSetIds.size}</span>
            )}
          </Button>
          <DensityToggle value={density} onChange={onDensity} />
          <ColumnsMenu {...menuProps} />
        </div>
      </div>

      <Card padded={false} className='data-setup-page__panel'>
        <DataTable
          columns={visibleColumns}
          rows={items}
          rowKey={(f) => f.id}
          density={density}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1100}
          empty={
            <>
              <Icon name='database' size={26} />
              {t('data_setup_meta_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={onLimit}
          perPageLabel={t('data_setup_items_per_page')}
          rangeText={t('data_setup_meta_range', { from, to, total })}
        />
      </Card>

      <ExportSettingsDrawer
        open={sqlSettingsOpen}
        onClose={() => setSqlSettingsOpen(false)}
        sections={COMPANY_EXPORT_SECTIONS}
        icon='database'
        title={t('data_setup_sql_title')}
        subtitle={t('data_setup_sql_subtitle')}
      />

      <PowerBiViewsModal open={powerBiOpen} onClose={() => setPowerBiOpen(false)} />

      <ManageSetsModal
        open={manageSetsOpen}
        onClose={() => setManageSetsOpen(false)}
        sets={formSets}
        loading={setsLoading}
        error={setsError}
        enabled={enabledSetIds}
        onApply={applySets}
      />

      {reimportTarget && (
        <ConfirmModal
          open
          onClose={() => setReimportTarget(null)}
          onConfirm={runReimport}
          tone='brand'
          icon='refresh-ccw'
          title={t('data_setup_meta_reimport_title', { name: reimportTarget.title })}
          description={t('data_setup_meta_reimport_body', {
            table: reimportTarget.dbName ?? '',
          })}
          confirmLabel={t('data_setup_meta_reimport')}
          loading={reimporting}
        />
      )}

      {confirm && (
        <ConfirmModal
          open
          onClose={() => setConfirm(null)}
          onConfirm={runConfirm}
          tone={confirm.next ? 'brand' : 'danger'}
          icon={confirm.next ? 'plug-zap' : 'plug'}
          title={t(
            confirm.next ? 'data_setup_meta_connect_title' : 'data_setup_meta_disconnect_title',
            { name: confirm.form.title },
          )}
          description={t(
            confirm.next ? 'data_setup_meta_connect_body' : 'data_setup_meta_disconnect_body',
            { table: dbNameOf(confirm.form) },
          )}
          confirmLabel={t(
            confirm.next ? 'data_setup_meta_connect_action' : 'data_setup_meta_disconnect_action',
          )}
          confirmVariant={confirm.next ? 'primary' : 'danger'}
          loading={connecting || disconnecting}
        />
      )}
    </>
  );
};

export default FormMetaTab;
